import { useState, useEffect, useRef, useMemo } from "react";
import { Check, Copy, Send, ExternalLink } from "lucide-react";
import type { GiftTheme } from "@/lib/gift-utils";
import { lightTheme, adjustColor } from "@/lib/gift-utils";
import { trackEvent } from "@/lib/analytics";

interface GiftShareBoosterProps {
  pageUrl: string;
  heroName: string;
  theme?: GiftTheme;
  giftId?: string;
  accentColor?: string;
  isDark?: boolean;
  slug?: string;
  playCount?: number;
  completionPercent?: number;
}

const VKIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" className="shrink-0">
    <path d="M12.785 16.241s.288-.032.436-.192c.136-.148.132-.427.132-.427s-.02-1.304.587-1.496c.598-.188 1.368 1.259 2.184 1.814.616.42 1.084.328 1.084.328l2.178-.03s1.14-.07.6-.964c-.044-.073-.316-.661-1.624-1.868-1.37-1.264-1.186-1.06.463-3.248.504-.668 1.748-2.146 1.592-2.622-.148-.444-1.058-.328-1.058-.328l-2.454.016s-.182-.025-.318.056c-.132.08-.216.264-.216.264s-.388 1.032-.904 1.912c-1.092 1.86-1.528 1.96-1.708 1.844-.42-.272-.314-1.1-.314-1.684 0-1.832.278-2.596-.542-2.792-.272-.066-.472-.108-1.168-.116-.892-.008-1.648.004-2.076.212-.284.138-.504.446-.37.464.166.022.54.1.738.372.256.348.248 1.136.248 1.136s.148 2.156-.344 2.424c-.338.184-.8-.192-1.796-1.916-.51-.882-.894-1.856-.894-1.856s-.074-.182-.206-.28c-.16-.118-.382-.156-.382-.156l-2.334.016s-.35.01-.478.162c-.114.136-.01.416-.01.416s1.82 4.258 3.88 6.404c1.888 1.968 4.032 1.838 4.032 1.838h.972z" />
  </svg>
);

const TelegramIcon = () => (
  <Send className="w-[18px] h-[18px] shrink-0" />
);

const getDeviceType = () => {
  const ua = navigator.userAgent;
  if (/iPad|Android(?!.*Mobile)/i.test(ua)) return "tablet";
  if (/Mobile|iPhone|iPod|Android/i.test(ua)) return "mobile";
  return "desktop";
};

const GiftShareBooster = ({
  pageUrl,
  heroName,
  theme = lightTheme,
  giftId,
  accentColor,
  slug,
  isDark,
  playCount = 0,
  completionPercent = 0,
}: GiftShareBoosterProps) => {
  const [copied, setCopied] = useState(false);
  const [showNudge, setShowNudge] = useState(false);
  const [blockViewed, setBlockViewed] = useState(false);
  const blockRef = useRef<HTMLDivElement>(null);

  const accent = isDark ? "#D4AF37" : (accentColor || "#6C63FF");
  const style = isDark ? "black" : "light";
  const deviceType = getDeviceType();

  const shareText = `Мне подарили персональную песню 🎵\nСлушай мой музыкальный подарок:`;

  const ogUrl = useMemo(() => {
    if (!slug) return pageUrl;
    const projectId = import.meta.env.VITE_SUPABASE_PROJECT_ID;
    if (!projectId) return pageUrl;
    return `https://${projectId}.supabase.co/functions/v1/gift-og?slug=${slug}`;
  }, [slug, pageUrl]);

  // Track block viewed
  useEffect(() => {
    if (!blockRef.current || blockViewed) return;
    const obs = new IntersectionObserver(([e]) => {
      if (e.isIntersecting) {
        setBlockViewed(true);
        trackEvent("gift_share_block_viewed", { gift_id: giftId, style, device_type: deviceType });
        obs.disconnect();
      }
    }, { threshold: 0.5 });
    obs.observe(blockRef.current);
    return () => obs.disconnect();
  }, [blockViewed, giftId, style, deviceType]);

  // Show nudge after copy
  useEffect(() => {
    if (!copied) return;
    const t = setTimeout(() => setShowNudge(true), 3000);
    return () => clearTimeout(t);
  }, [copied]);

  // Glow highlight when played multiple times or listened to 80%+
  const isHighlighted = playCount > 1 || completionPercent >= 80;

  const handleVKShare = () => {
    trackEvent("gift_share_vk_clicked", { gift_id: giftId, style, device_type: deviceType, play_count: playCount, completion_percent: completionPercent });

    const shareUrl = pageUrl;
    const webUrl = `https://vk.com/share.php?url=${encodeURIComponent(shareUrl)}&comment=${encodeURIComponent(shareText)}`;

    // On mobile, try to open the native VK app first via vk:// scheme
    if (deviceType === "mobile" || deviceType === "tablet") {
      const appUrl = `vk://vk.com/share.php?url=${encodeURIComponent(shareUrl)}&comment=${encodeURIComponent(shareText)}`;
      const start = Date.now();
      // If the app opens, the page will blur/hide. If not, we fallback after timeout.
      const fallbackTimer = setTimeout(() => {
        // If less than 2s passed, the app didn't open — fallback to browser
        if (Date.now() - start < 2500) {
          window.open(webUrl, "_blank", "noopener,noreferrer");
          trackEvent("gift_share_vk_fallback_web", { gift_id: giftId, style, device_type: deviceType });
        }
      }, 800);

      // Listen for page visibility change — if app opened, cancel fallback
      const onVisibilityChange = () => {
        if (document.hidden) {
          clearTimeout(fallbackTimer);
          trackEvent("gift_share_vk_app_opened", { gift_id: giftId, style, device_type: deviceType });
          document.removeEventListener("visibilitychange", onVisibilityChange);
        }
      };
      document.addEventListener("visibilitychange", onVisibilityChange);
      
      // Also clean up listener after timeout
      setTimeout(() => {
        document.removeEventListener("visibilitychange", onVisibilityChange);
      }, 3000);

      window.location.href = appUrl;
    } else {
      // Desktop — just open in new tab
      try {
        window.open(webUrl, "_blank", "noopener,noreferrer");
        trackEvent("gift_share_vk_opened", { gift_id: giftId, style, device_type: deviceType });
      } catch {
        trackEvent("gift_share_vk_failed", { gift_id: giftId, style, device_type: deviceType });
      }
    }
  };

  const handleTGShare = (e: React.MouseEvent) => {
    e.preventDefault();
    trackEvent("gift_share_tg_clicked", { gift_id: giftId, style, device_type: deviceType, play_count: playCount, completion_percent: completionPercent });

    const webUrl = `https://t.me/share/url?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(shareText)}`;

    if (deviceType === "mobile" || deviceType === "tablet") {
      const appUrl = `tg://msg_url?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(shareText)}`;
      const start = Date.now();
      const fallbackTimer = setTimeout(() => {
        if (Date.now() - start < 2500) {
          window.open(webUrl, "_blank", "noopener,noreferrer");
          trackEvent("gift_share_tg_fallback_web", { gift_id: giftId, style, device_type: deviceType });
        }
      }, 800);

      const onVisibilityChange = () => {
        if (document.hidden) {
          clearTimeout(fallbackTimer);
          trackEvent("gift_share_tg_app_opened", { gift_id: giftId, style, device_type: deviceType });
          document.removeEventListener("visibilitychange", onVisibilityChange);
        }
      };
      document.addEventListener("visibilitychange", onVisibilityChange);
      setTimeout(() => document.removeEventListener("visibilitychange", onVisibilityChange), 3000);

      window.location.href = appUrl;
    } else {
      window.open(webUrl, "_blank", "noopener,noreferrer");
      trackEvent("gift_share_tg_opened", { gift_id: giftId, style, device_type: deviceType });
    }
  };

  const handleCopy = async () => {
    await navigator.clipboard.writeText(pageUrl);
    setCopied(true);
    trackEvent("gift_share_link_copied", { gift_id: giftId, style, device_type: deviceType, play_count: playCount, completion_percent: completionPercent });
    setTimeout(() => setCopied(false), 2000);
  };

  const handleCreateOwn = () => {
    trackEvent("gift_share_create_own_clicked", { gift_id: giftId, style, device_type: deviceType, play_count: playCount, completion_percent: completionPercent });
  };

  if (completionPercent >= 80 && playCount > 0) {
    trackEvent("gift_share_after_full_play", { gift_id: giftId, style, device_type: deviceType });
  }

  const cardBg = isDark ? "rgba(18,18,32,0.6)" : "rgba(255,255,255,0.6)";
  const cardBorder = isDark ? `1px solid rgba(212,175,55,0.12)` : `1px solid rgba(108,99,255,0.08)`;
  const glowShadow = isHighlighted
    ? isDark
      ? `0 0 30px rgba(212,175,55,0.08), 0 8px 32px rgba(0,0,0,0.2)`
      : `0 0 30px rgba(108,99,255,0.08), 0 8px 32px rgba(108,99,255,0.06)`
    : isDark
      ? "0 8px 32px rgba(0,0,0,0.2)"
      : "0 8px 32px rgba(108,99,255,0.04)";

  return (
    <div ref={blockRef} className="w-full flex flex-col items-center gap-4">
      {/* Share Booster Card */}
      <div
        className="w-full rounded-[20px] px-5 py-5 backdrop-blur-lg transition-shadow duration-700"
        style={{
          background: cardBg,
          border: cardBorder,
          boxShadow: glowShadow,
        }}
      >
        {/* Header */}
        <div className="text-center mb-4">
          <p
            className="text-[16px] font-semibold leading-snug"
            style={{ color: isDark ? "rgba(255,255,255,0.92)" : "#1A1A2E" }}
          >
            Поделись этим вау-моментом ✨
          </p>
          <p
            className="text-[13px] mt-1"
            style={{ color: isDark ? "rgba(255,255,255,0.45)" : "rgba(26,26,46,0.5)" }}
          >
            Пусть друзья тоже удивятся такому подарку
          </p>
        </div>

        {/* Main share buttons */}
        <div className="grid grid-cols-2 gap-2.5 mb-3">
          {/* Telegram */}
          <button
            onClick={handleTGShare}
            className="flex items-center justify-center gap-2 py-3 rounded-[14px] text-[13px] font-medium transition-all duration-200 hover:scale-[1.03] active:scale-[0.97]"
            style={{
              background: isDark ? "rgba(255,255,255,0.06)" : "rgba(26,26,46,0.04)",
              color: isDark ? "rgba(255,255,255,0.75)" : "rgba(26,26,46,0.7)",
              border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(26,26,46,0.08)"}`,
            }}
          >
            <TelegramIcon />
            <span>Telegram</span>
          </button>

          {/* VK */}
          <button
            onClick={handleVKShare}
            className="flex items-center justify-center gap-2 py-3 rounded-[14px] text-[13px] font-medium transition-all duration-200 hover:scale-[1.03] active:scale-[0.97]"
            style={{
              background: isDark ? "rgba(255,255,255,0.06)" : "rgba(26,26,46,0.04)",
              color: isDark ? "rgba(255,255,255,0.75)" : "rgba(26,26,46,0.7)",
              border: `1px solid ${isDark ? "rgba(255,255,255,0.08)" : "rgba(26,26,46,0.08)"}`,
            }}
          >
            <VKIcon />
            <span>ВКонтакте</span>
          </button>
        </div>

        {/* Copy link button */}
        <button
          onClick={handleCopy}
          className="w-full flex items-center justify-center gap-2 py-2.5 rounded-[12px] text-[12px] font-medium transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
          style={{
            background: copied
              ? `${accent}15`
              : "transparent",
            color: copied ? accent : (isDark ? "rgba(255,255,255,0.4)" : "rgba(26,26,46,0.4)"),
            border: `1px solid ${copied ? `${accent}30` : (isDark ? "rgba(255,255,255,0.06)" : "rgba(26,26,46,0.06)")}`,
          }}
        >
          {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
          <span>{copied ? "Ссылка скопирована ✅" : "Скопировать ссылку"}</span>
        </button>

        {/* Post-copy nudge */}
        {showNudge && (
          <p
            className="text-[11px] text-center mt-2 animate-fade-in"
            style={{ color: isDark ? "rgba(255,255,255,0.35)" : "rgba(26,26,46,0.35)" }}
          >
            Отправь ссылку другу — пусть тоже удивится 🎵
          </p>
        )}

        {/* Social proof trigger */}
        <div className="mt-4 pt-3" style={{ borderTop: `1px solid ${isDark ? "rgba(255,255,255,0.05)" : "rgba(26,26,46,0.05)"}` }}>
          <p
            className="text-[11px] text-center"
            style={{ color: isDark ? "rgba(255,255,255,0.3)" : "rgba(26,26,46,0.3)" }}
          >
            💡 Более 78% получателей делятся этим подарком
          </p>
        </div>
      </div>

      {/* Create Your Own CTA */}
      <div
        className="w-[95%] rounded-[18px] px-5 py-4 text-center"
        style={{
          background: isDark ? "rgba(14,14,22,0.8)" : "rgba(245,245,248,0.9)",
          border: cardBorder,
        }}
      >
        <p
          className="text-[14px] font-semibold mb-0.5"
          style={{ color: isDark ? "rgba(255,255,255,0.92)" : "#1A1A2E" }}
        >
          ✨ Хочешь такой же подарок?
        </p>
        <p
          className="text-[12px] mb-3"
          style={{ color: isDark ? "rgba(255,255,255,0.45)" : "rgba(26,26,46,0.5)" }}
        >
          Создай персональный трек за 3 минуты
        </p>
        <a
          href="/create"
          onClick={handleCreateOwn}
          className="inline-flex items-center gap-1.5 px-6 py-2.5 rounded-full text-white text-[13px] font-semibold transition-all duration-200 hover:scale-[1.03] active:scale-[0.97]"
          style={{
            background: `linear-gradient(135deg, ${accent}, ${adjustColor(accent, -30)})`,
            boxShadow: `0 4px 20px ${accent}20`,
          }}
        >
          🚀 Создать свой хит
          <ExternalLink className="w-3.5 h-3.5 opacity-60" />
        </a>
      </div>
    </div>
  );
};

export default GiftShareBooster;
